import React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';

import {CreateSchemaContext} from "./context/CreateSchemaContext";
import {WizardStepProps} from "../../wizard/Wizard";
import SchemaPlayground from "../playground/SchemaPlayground";
import {UnsavedChangesConfirmation} from "../../../common/NavigationConfirmation";
import {SchemaExistsConfirmation} from "../common/SchemaExistsConfirmation";
import {DialogService} from "../../../common/Dialog";

type SaveSchemaStepState = {
  hasChanges: boolean
}

class SaveSchemaStep extends React.Component<WizardStepProps, SaveSchemaStepState> {

  static contextType = CreateSchemaContext;

  constructor(props: WizardStepProps) {
    super(props);
    this.state = {
      hasChanges: false
    };
    this.onDismissGenerationWarnings = this.onDismissGenerationWarnings.bind(this);
  }

  /**
   * Saves or updates the schema and proceed with the next step if operation is successful.
   */
  onSave = () => {
    const result = this.context.saveSchema(this.context.schema);
    return this.props.next(result);
  };

  /**
   * Handles changes in the soml schema string which might happen in the schema editor component.
   * Schedules a schema context property change operation through the context.
   * @param {string} schema The updated schema string.
   * @param {boolean} dirty a flag from the editor telling if the schema is changed or not
   */
  onSchemaChange = (schema: string, dirty: boolean) => {
    this.setState({
      hasChanges: dirty
    });
    this.context.changeSchema(schema);
  };

  onDismissGenerationWarnings = () => {
    this.context.removeGenerationWarnings();
  };

  /**
   * Performs schema update and clears the error. The next step is opened on success.
   */
  confirmOverride = () => {
    // If override is confirmed, the schema have to be updated.
    const result: boolean = this.context.updateSchema(this.context.schemaId, this.context.schema);
    this.context.removeError();
    return this.props.next(result);
  };

  denyOverride = () => {
    // Override is denied so just clear the error.
    this.context.removeError();
  };

  onStepBack = () => {
    if (this.state.hasChanges) {
      DialogService.confirm({
        message: 'The schema has changes which will be lost! Are you sure you want to go back?',
        onConfirm: this.onStepBackConfirm
      });
    } else {
      this.props.prev();
    }
  };

  onStepBackConfirm = () => {
    this.props.prev();
  };

  render() {
    const {schema, schemaId, generationWarnings, error, isRequestRunning} = this.context;
    return (
      <>
        <Container className="SaveSchemaStep no-padding" fluid>
          <Row>
            <Col>
              <SchemaGenerationWarnings generationWarnings={generationWarnings}
                                        onClose={this.onDismissGenerationWarnings}/>
            </Col>
          </Row>
          <Row className="pb-3">
            <Col>
              <div className="StepActions">
                <Button className="prev-btn"
                        onClick={this.onStepBack}
                        disabled={isRequestRunning}
                        title="Generate Schema">
                  Generate
                </Button>
                <Button className="save-schema next-btn"
                        onClick={this.onSave}
                        disabled={isRequestRunning}
                        title="Save Schema">
                  Save
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <SchemaPlayground schema={schema}
                                actionsDisabled={isRequestRunning}
                                onChange={this.onSchemaChange}
                                errors={error}/>
            </Col>
          </Row>
        </Container>
        <UnsavedChangesConfirmation when={this.state.hasChanges}/>
        <SchemaExistsConfirmation schemaId={schemaId} error={error}
                                  onConfirm={this.confirmOverride}
                                  onCancel={this.denyOverride}/>
      </>
    );
  }
}

function SchemaGenerationWarnings(props: { generationWarnings: Array<string>, onClose: () => void }) {
  if (props.generationWarnings && props.generationWarnings.length) {
    return (
      <Alert variant="warning" className="SchemaGenerationWarnings" onClose={() => props.onClose()} dismissible>
        <ul>
          {
            props.generationWarnings.map((warning: string, index: number) => <li key={index}>{warning}</li>)
          }
        </ul>
      </Alert>
    );
  }
  return null;
}

export default SaveSchemaStep;
